import React, { useMemo, type PropsWithChildren, type MouseEvent } from 'react';

import { cssMap } from '@atlaskit/css';
import GrowVerticalIcon from '@atlaskit/icon/core/grow-vertical';
import AvatarGroup from '@atlaskit/avatar-group';
import { token } from '@atlaskit/tokens';
import { Inline, Pressable, Text, Box } from '@atlaskit/primitives/compiled';

import { WithCurvedBranchingStyle } from '@confluence/inline-comments-common/entry-points/components.ts';
import type { ReplyData } from '@confluence/comments-data/entry-points/useCommentsData.ts';

import {
	getProfilePicturesOfHiddenReplies,
	type ProfilePictureInfo,
} from '../helpers/repliesHelpers.ts';

const styles = cssMap({
	dot: {
		height: '8px',
		width: '8px',
		backgroundColor: token('color.background.brand.bold'),
		borderRadius: token('radius.full'),
		margin: token('space.050'),
	},
	pressableContainer: {
		marginLeft: token('space.200'),
		paddingLeft: token('space.100'),
	},
	pressableContainerWithBranch: {
		marginLeft: token('space.200'),
		paddingLeft: token('space.100'),
		position: 'relative',
		'&::before': {
			content: '""',
			position: 'absolute',
			height: '100%',
			left: 0,
			// @ts-expect-error: The color of the border is not supported by our types, but it's what we're using.
			borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		},
	},
	pressableWithBranch: {
		width: '100%',
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.neutral.subtle'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.0'),
		borderRadius: token('radius.small'),
		userSelect: 'none',
		fontWeight: token('font.weight.medium'),
		'&:hover': {
			cursor: 'pointer',
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	pressable: {
		width: '100%',
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.neutral.subtle'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
		borderRadius: token('radius.small'),
		userSelect: 'none',
		fontWeight: token('font.weight.medium'),
		'&:hover': {
			cursor: 'pointer',
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	icon: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		// @ts-ignore
		left: '1px',
	},
});

const SHOW_HIDDEN_REPLIES = 'show-hidden-replies';

type RepliesTogglerButtonContentProps = {
	showHiddenReplies: (e: MouseEvent<HTMLElement>) => void;
	hiddenReplies: ReplyData[];
	hasHiddenUnreadReplies: boolean;
	analyticsContext: Record<string, any>;
	message: string;
	isBranchHidden: boolean;
};

const RepliesTogglerButtonContent = ({
	showHiddenReplies,
	hiddenReplies,
	hasHiddenUnreadReplies,
	analyticsContext,
	message,
	children: icon,
	isBranchHidden,
}: PropsWithChildren<RepliesTogglerButtonContentProps>) => {
	const profilePicturesHiddenReplies: ProfilePictureInfo[] = useMemo(
		() => getProfilePicturesOfHiddenReplies(hiddenReplies),
		[hiddenReplies],
	);

	return (
		<Pressable
			onClick={showHiddenReplies}
			xcss={isBranchHidden ? styles.pressable : styles.pressableWithBranch}
			analyticsContext={analyticsContext}
			testId={SHOW_HIDDEN_REPLIES}
			interactionName={SHOW_HIDDEN_REPLIES}
		>
			<Inline alignBlock="center" space="space.100" spread="space-between">
				<Inline alignBlock="center" space="space.100">
					{isBranchHidden && (
						<Box as="span" xcss={styles.icon} testId="icon--grow-vertical">
							{icon}
						</Box>
					)}
					<Box as="span">
						<AvatarGroup
							onMoreClick={() => {}}
							data={profilePicturesHiddenReplies.map(({ displayName, profilePicturePath }) => ({
								name: displayName,
								src: profilePicturePath,
							}))}
							maxCount={3}
							appearance="stack"
							size="small"
							shouldPopupRenderToParent
							testId="replies-toggler-profile-pictures"
						/>
					</Box>
					<Text color="color.text.subtle" weight="medium">
						{message}
					</Text>
				</Inline>
				{hasHiddenUnreadReplies && (
					<Box as="span" xcss={styles.dot} testId="unread-comment-replies-indicator" />
				)}
			</Inline>
		</Pressable>
	);
};

const curvedBranchCustomStyles = {
	height: token('space.300'),
	left: token('space.negative.150'),
};

export type RepliesTogglerButtonProps = Omit<
	RepliesTogglerButtonContentProps & {
		isHiddenReplyView: boolean;
		expandReplyEditor: boolean;
	},
	'isBranchHidden'
>;

export const RepliesTogglerButton = ({
	hiddenReplies,
	showHiddenReplies,
	hasHiddenUnreadReplies,
	analyticsContext,
	message,
	isHiddenReplyView,
	expandReplyEditor,
}: RepliesTogglerButtonProps) => {
	const isBranchHidden = !isHiddenReplyView || (isHiddenReplyView && expandReplyEditor);

	const buttonContent = (
		<RepliesTogglerButtonContent
			isBranchHidden={isBranchHidden}
			hiddenReplies={hiddenReplies}
			showHiddenReplies={showHiddenReplies}
			hasHiddenUnreadReplies={hasHiddenUnreadReplies}
			analyticsContext={analyticsContext}
			message={message}
		>
			<GrowVerticalIcon label="" />
		</RepliesTogglerButtonContent>
	);

	return isBranchHidden ? (
		buttonContent
	) : (
		<Box
			testId="curved-branching-wrapper"
			xcss={expandReplyEditor ? styles.pressableContainerWithBranch : styles.pressableContainer}
		>
			<WithCurvedBranchingStyle showDefaultStyles customStyles={curvedBranchCustomStyles}>
				{buttonContent}
			</WithCurvedBranchingStyle>
		</Box>
	);
};
